import { Box, Checkbox, type CheckboxProps } from '@mantine/core';
import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { useRules } from './hooks';
import type { RequiredField } from './types';

/**
 * CheckboxFieldProps
 * @public
 */
export type CheckboxFieldProps<T extends FieldValues> = UseControllerProps<T> &
  CheckboxProps &
  RequiredField & {
    box?: boolean;
    checkValue?: any;
    unCheckValue?: any;
  };

/**
 * CheckboxField component
 * @public
 *
 * @param name - name of the field
 * @param rules - validation rules
 * @param shouldUnregister - unregister field when unmount
 * @param defaultValue - default value
 * @param control - form control
 * @param required - is field required
 * @param box - render checkbox as a box
 * @param checkValue - value it to set when checkbox is checked
 * @param unCheckValue - value it to set when checkbox is unchecked
 * @param restProps - props
 */
export function CheckboxField<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  required,
  box,
  checkValue,
  unCheckValue,
  ...restProps
}: CheckboxFieldProps<T>) {
  const innerRules = useRules({ required, ...rules });

  const { field, fieldState } = useController<T>({
    name,
    control,
    defaultValue,
    rules: innerRules,
    shouldUnregister,
  });

  const { onChange, value, ...restFieldProps } = field;

  /**
   * Handle checkbox change event.
   * @param e - Change event.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (checkValue === undefined && unCheckValue === undefined) {
      onChange(e);
      return;
    }

    onChange(e.target.checked ? checkValue : unCheckValue);
  };

  /**
   * Determine if the checkbox is checked based on the field value and checkValue.
   */
  const checked =
    typeof field.value === 'boolean'
      ? field.value
      : field.value === checkValue && field.value !== undefined;

  /**
   * Render the checkbox component.
   */
  const CheckboxRender = (
    <Checkbox
      sx={{
        margin: 0,
      }}
      checked={checked}
      error={fieldState.error?.message}
      onChange={handleChange}
      value={value === null ? undefined : value}
      {...restFieldProps}
      {...restProps}
    />
  );

  if (!box) {
    return CheckboxRender;
  }

  return (
    <Box
      component="label"
      sx={theme => ({
        background: field.value ? theme.colors.violet[1] : theme.colors.white,
        border: field.value
          ? `solid 1px ${theme.colors.violet[0]}`
          : `solid 1px ${theme.colors.gray[3]}`,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.sm,
        padding: theme.spacing.sm,
        borderRadius: theme.radius.sm,
        '&:hover': {
          cursor: 'pointer',
        },
      })}
    >
      {CheckboxRender}
    </Box>
  );
}
